import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skull, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const CZ_DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
function fmt(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${CZ_DAYS[d.getDay()]} ${dateStr}`;
}

export default function CancelReservation() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | found | cancelling | done | already | notfound
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get(`/reservations/by-token/${token}`)
      .then(({ data }) => {
        setRes(data);
        setState(data.status === "cancelled" ? "already" : "found");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  const cancel = async () => {
    setState("cancelling");
    try {
      const { data } = await api.post(`/reservations/cancel/${token}`);
      setState(data.already ? "already" : "done");
    } catch (e) {
      setState("notfound");
    }
  };

  return (
    <div className="grain min-h-screen bg-[#050505] flex items-center justify-center px-5 relative">
      <div className="absolute inset-0 scanlines pointer-events-none" />
      <div className="relative z-10 w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8 border-t-2 border-t-red-700 text-center text-neutral-200">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Skull className="w-6 h-6 text-red-600" />
          <h1 className="font-head text-xl uppercase tracking-widest">Zrušení rezervace</h1>
        </div>

        {state === "loading" && <Loader2 className="w-10 h-10 text-red-600 animate-spin mx-auto my-8" />}

        {state === "notfound" && (
          <div className="py-6">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-neutral-400">Rezervace nebyla nalezena nebo odkaz už není platný.</p>
          </div>
        )}

        {state === "already" && (
          <div className="py-6" data-testid="cancel-already">
            <CheckCircle2 className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
            <p className="text-neutral-400">Tato rezervace už byla zrušena.</p>
          </div>
        )}

        {(state === "found" || state === "cancelling") && res && (
          <div className="py-4">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <p className="text-neutral-300 mb-2">Opravdu chcete zrušit svou rezervaci?</p>
            <div className="border border-white/10 bg-[#111] p-4 my-4 text-sm">
              <p className="text-neutral-200 font-bold">{res.first_name} {res.last_name}</p>
              <p className="text-neutral-400 mt-1">{fmt(res.date)} · {res.time}</p>
            </div>
            <Button data-testid="confirm-cancel-btn" onClick={cancel} disabled={state === "cancelling"}
              className="vhs-btn w-full rounded-none bg-red-700 hover:bg-red-600 text-white font-head tracking-widest py-6">
              {state === "cancelling" ? <Loader2 className="w-4 h-4 animate-spin" /> : "ZRUŠIT REZERVACI"}
            </Button>
            <a href="/" className="block text-xs text-neutral-600 hover:text-red-500 mt-4 tracking-widest">← ZPĚT NA WEB</a>
          </div>
        )}

        {state === "done" && (
          <div className="py-6" data-testid="cancel-done">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4 glow-red" />
            <h3 className="font-head text-xl uppercase text-neutral-100 mb-2">Rezervace zrušena</h3>
            <p className="text-sm text-neutral-400">Váš termín byl uvolněn. Děkujeme, že jste dali vědět.</p>
            <a href="/" className="inline-block mt-6"><Button className="vhs-btn rounded-none bg-red-700 hover:bg-red-600">Zpět na web</Button></a>
          </div>
        )}
      </div>
    </div>
  );
}
